import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { addCartItem } from "../redux/productSlice";
import { Heart } from "lucide-react";

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [instructions, setInstructions] = useState("");

  const handleChoiceChange = (optionTitle, choice, multiple) => {
    if (multiple) {
      setSelectedChoices((prev) => {
        const prevChoices = prev[optionTitle] || [];
        const isSelected = prevChoices.some((c) => c.id === choice.id);
        const newChoices = isSelected
          ? prevChoices.filter((c) => c.id !== choice.id)
          : [...prevChoices, choice];
        return { ...prev, [optionTitle]: newChoices };
      });
    } else {
      setSelectedChoices((prev) => ({
        ...prev,
        [optionTitle]: choice,
      }));
    }
  };

  const calculateTotalPrice = () => {
    const optionsPrice = Object.values(selectedChoices).reduce((acc, val) => {
      if (Array.isArray(val)) {
        return acc + val.reduce((sum, c) => sum + Number(c.price), 0);
      }
      return acc + Number(val.price || 0);
    }, 0);
    return Number(product.price) + optionsPrice;
  };

  const handleAddToCart = () => {
    if (!product) return;

    const cartItem = {
      ...product,
      selectedOptions: selectedChoices,
      specialInstructions: instructions,
      finalPrice: calculateTotalPrice(),
      qty: 1,
    };
    dispatch(addCartItem(cartItem));
    setModalOpen(false);
  };

  return (
    <>
      {/* Product Card */}
      <div
        className="bg-white rounded-[1.5rem] shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer w-52 flex flex-col"
        onClick={() => setModalOpen(true)}
      >
        {/* Image Section */}
        <div className="relative w-full h-56 overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
          <button
            className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition"
            aria-label="Add to wishlist"
          >
            <Heart className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Info Section */}
        <div className="p-5 flex flex-col flex-grow">
          <h3 className="text-xl font-serif font-normal text-gray-900 mb-1">
            {product.name}
          </h3>
          <p className="text-gray-600 text-sm mb-1 italic capitalize"></p>
          <p className="text-gray-700 text-sm leading-snug mb-3 line-clamp-3">
            {product.description || "Delicious treat made with love."}
          </p>

          <div className="mt-auto flex justify-between items-center">
            <p className="text-lg font-bold text-[#D2691E]">
              CAD {product.price}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
              className="bg-[#FFB4AB] hover:bg-[#fb887b] text-white text-sm font-semibold px-4 py-2 rounded-full transition-all"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 px-2 sm:px-4">
          <div
            className="bg-white w-full max-w-lg sm:rounded-2xl rounded-xl shadow-2xl overflow-hidden animate-fadeIn 
                       max-h-[70vh] overflow-y-auto scrollbar-none"
          >
            <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                {product.name}
              </h2>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">{product.description}</p>

              {/* Options */}
              {product.options?.length > 0 && (
                <div className="space-y-4">
                  {product.options.map((opt) => (
                    <div key={opt._id}>
                      <p className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
                        {opt.title}
                      </p>
                      <div className="flex flex-col gap-2">
                        {opt.choices.map((choice) => (
                          <label
                            key={choice.id}
                            className="flex items-center gap-2 text-gray-700 text-sm"
                          >
                            <input
                              type={opt.multiple ? "checkbox" : "radio"}
                              name={opt.title}
                              value={choice.id}
                              checked={
                                opt.multiple
                                  ? selectedChoices[opt.title]?.some(
                                      (c) => c.id === choice.id
                                    )
                                  : selectedChoices[opt.title]?.id ===
                                    choice.id
                              }
                              onChange={() =>
                                handleChoiceChange(
                                  opt.title,
                                  choice,
                                  opt.multiple
                                )
                              }
                            />
                            {choice.name}{" "}
                            {choice.price > 0 && (
                              <span className="text-green-600 text-xs sm:text-sm">
                                (+CAD {choice.price})
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Special Instructions */}
              <div className="mt-5">
                <label className="block font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                  Special Instructions
                </label>
                <textarea
                  placeholder="Write Happy Birthday Name, etc."
                  className="border w-full p-2 rounded-lg text-sm focus:ring-2 focus:ring-[#FFB4AB] outline-none"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>
             <span className=" h-4 text-sm text-gray-700">
  ⚠️ Note: Please mention if you have any allergies in special instructions.
</span>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3 sm:gap-0">
                <p className="text-lg sm:text-xl font-bold text-gray-900">
                  Total: CAD {calculateTotalPrice()}
                </p>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="w-1/2 sm:w-auto px-4 py-2 bg-gray-300 text-gray-800 rounded-full hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToCart}
                    className="w-1/2 sm:w-auto px-4 py-2 bg-[#f67f6f] text-white rounded-full hover:bg-[#f36252]"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
